import {
  LOGOUT,
  GET_CONTACTS_SUCCESS,
  REFRESH_TOKEN_REQUEST,
  REFRESH_TOKEN_LOGIN_SUCCESS,
} from 'actions';
import models, { sessionManager, Models } from 'models';
import { ORMSession, ORMState } from 'models/externalTypes';
import { ModelType } from 'redux-orm';

interface ModelProxy {
  reducer(action: any, modelType: ModelType<any>, session: ORMSession): void;
}

const defaultUpdater = (session: ORMSession, action: any) => {
  session.sessionBoundModels.forEach((modelClass) => {
    type M = typeof modelClass;
    const model = modelClass as M & ModelProxy;

    if (typeof model.reducer === 'function') {
      model.reducer(action, modelClass, session);
    }
  });
};

const createReducer =
  (orm: Models, updater = defaultUpdater) =>
  (state: ORMState, action: any) => {
    const ormState =
      action.type === LOGOUT
        ? orm.getEmptyState()
        : state || orm.getEmptyState();

    /*
    session is provided by a manager instead of calling:
    orm.session(ormState) directly (except in the cases below).  This
    allows the session to be preserved across action dispatches - which
    is needed for batched processing of expensive ORM updates.
  */
    const session = sessionManager.initSession(ormState);
    const alternateSession = sessionManager.getAlternateSession();

    updater(session, action);
    switch (action.type) {
      /*
      During a batched update - every redux update will
      cause a progressive update to the redux state for the
      models being batch updated - causing re-renders etc
      for everything subscribed to those models.

      The folowing cover the most repeated actions that
      run repeatedly on a timer but don't update the ORM.
      We don't use the sessionManager created session, which
      allows the state update to go through without updating the
      part processed ORM models.
    */
      case REFRESH_TOKEN_LOGIN_SUCCESS:
      case REFRESH_TOKEN_REQUEST:
        return alternateSession.state;
      /*
    The following actions are also frequently dispatched, but
    update ORM state.  To handle this update during batching
    without updating the currently batching models, while also 
    preventing the  ORM session used for batching from becoming
    stale - we update both a fresh session and the batching session.
    */
      case GET_CONTACTS_SUCCESS: {
        return { ...session.state, Contact: { ...session.state.Contact } };
      }
      default:
        return session.state;
    }
  };

export default createReducer(models);
